import React, { useEffect, useState, useRef } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Typography,
    Box,
    Paper,
    CircularProgress,
    Switch,
    FormControlLabel,
    DialogContentText,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Menu,
    TextField,
    Autocomplete,
    LinearProgress
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faTrash,
    faEdit,
    faPlus,
    faFilePdf,
    faFolder,
    faFolderOpen,
    faPrint,
    faTimes,
    faLink,
    faUpload
} from '@fortawesome/free-solid-svg-icons'
import documentsRoutes from '../routes/documents.routes'
import MachinesService from '../services/machines.service'
import { useStatus, useConnectionContext } from '../context'
import { documentationDatabase } from '../indexedDB'
import { ConfirmarDialog } from './Confirmar.dialog'
import AlertDialog from './AlertDialog'
import SpmUploadDialog from './SpmUploadDialog.dialog'

const SpmDocumentsDialog = ({
    open,
    handleClose,
    spmCode = '',
    guide = '',
    readOnly = false
}) => {
    const { setStatus } = useStatus()
    const { isOnline } = useConnectionContext()
    const blobUrlsRef = useRef([])

    const revokeBlobUrls = () => {
        blobUrlsRef.current.forEach((url) => {
            try {
                URL.revokeObjectURL(url)
            } catch (_) {}
        })
        blobUrlsRef.current = []
    }

    useEffect(() => {
        return () => {
            revokeBlobUrls()
        }
    }, [])

    // Data lists
    const [spms, setSpms] = useState([])
    const [machines, setMachines] = useState([])

    // Selections
    const [selectedSpm, setSelectedSpm] = useState(null) // SpmDocument object
    const [selectedStepName, setSelectedStepName] = useState('')
    const [selectedFile, setSelectedFile] = useState(null) // File object

    // UI States
    const [loading, setLoading] = useState(false)
    const [editMode, setEditMode] = useState(false)
    const [fileUrl, setFileUrl] = useState('')

    // Upload State (Directo a Azure)
    const [spmUploadOpen, setSpmUploadOpen] = useState(false)
    const [spmUploadMode, setSpmUploadMode] = useState('folder') // 'folder' | 'file'
    const [uploading, setUploading] = useState(false) // Mantener para compatibilidad con guardias del diálogo

    // Diálogos personalizados de Confirmación y Alerta
    const [confirmConfig, setConfirmConfig] = useState({
        open: false,
        message: '',
        onConfirm: () => {},
        onCancel: () => {}
    })

    const [alertConfig, setAlertConfig] = useState({
        open: false,
        title: 'Atención',
        message: ''
    })

    const triggerConfirm = (message, onConfirm, onCancel = null) => {
        setConfirmConfig({
            open: true,
            message,
            onConfirm: () => {
                onConfirm()
                setConfirmConfig((prev) => ({ ...prev, open: false }))
            },
            onCancel: () => {
                if (onCancel) onCancel()
                setConfirmConfig((prev) => ({ ...prev, open: false }))
            }
        })
    }

    const triggerAlert = (message, title = 'Atención') => {
        setAlertConfig({
            open: true,
            title,
            message
        })
    }

    // Rename Modal State
    const [openRenameDialog, setOpenRenameDialog] = useState(false)
    const [renameType, setRenameType] = useState('') // 'spm' | 'step' | 'file'
    const [renameCurrentValue, setRenameCurrentValue] = useState('')
    const [renameNewValue, setRenameNewValue] = useState('')
    const [renameTarget, setRenameTarget] = useState(null) // { spm, stepName, filename }

    // Load initial data
    useEffect(() => {
        if (open) {
            loadSpmDocuments()
            loadMachines()
        }
    }, [open, spmCode])

    const loadSpmDocuments = async () => {
        setLoading(true)
        revokeBlobUrls()
        const newBlobUrls = []
        try {
            if (spmCode) {
                if (isOnline) {
                    const res = await documentsRoutes.getSpmDocumentSteps(spmCode)
                    let data = res.data
                    if (data && data.steps && guide && readOnly) {
                        const cleanGuide = guide.trim().toUpperCase()
                        data = {
                            ...data,
                            steps: data.steps.filter(
                                (s) => s.stepName.trim().toUpperCase() === cleanGuide
                            )
                        }
                    }
                    setSelectedSpm(data)
                } else {
                    await documentationDatabase.initDbDocumentation()
                    const localDoc = await documentationDatabase.getSpmStructure(spmCode)
                    if (localDoc) {
                        let stepsWithLocalUrls = await Promise.all(
                            localDoc.steps.map(async (step) => {
                                const filesWithLocalUrls = await Promise.all(
                                    step.files.map(async (file) => {
                                        const fileData = await documentationDatabase.getPdfFile(
                                            file.blobPath
                                        )
                                        if (fileData && fileData.blob) {
                                            const localUrl = URL.createObjectURL(fileData.blob)
                                            newBlobUrls.push(localUrl)
                                            return {
                                                ...file,
                                                url: localUrl
                                            }
                                        }
                                        return file
                                    })
                                )
                                return {
                                    ...step,
                                    files: filesWithLocalUrls
                                }
                            })
                        )
                        if (guide && readOnly) {
                            const cleanGuide = guide.trim().toUpperCase()
                            stepsWithLocalUrls = stepsWithLocalUrls.filter(
                                (s) => s.stepName.trim().toUpperCase() === cleanGuide
                            )
                        }
                        setSelectedSpm({
                            ...localDoc,
                            steps: stepsWithLocalUrls
                        })
                    } else {
                        setSelectedSpm(null)
                    }
                }
            } else {
                if (isOnline) {
                    const res = await documentsRoutes.getAllSpmDocuments()
                    setSpms(res.data || [])
                } else {
                    await documentationDatabase.initDbDocumentation()
                    const localDocs = await documentationDatabase.getAllSpmStructures()
                    setSpms(localDocs || [])
                }
            }
        } catch (error) {
            console.error('Error cargando documentos SPM:', error)
        } finally {
            if (newBlobUrls.length > 0) {
                blobUrlsRef.current.push(...newBlobUrls)
            }
            setLoading(false)
        }
    }

    const loadMachines = async () => {
        try {
            // Cargar máquinas activas desde el servicio (online mode, admin mode)
            const machinesData = await MachinesService.getMachines(
                true,
                false,
                true,
                false,
                false
            )
            setMachines(machinesData || [])
        } catch (error) {
            console.error('Error cargando máquinas:', error)
        }
    }

    // Obtener lista de pasos existentes para el SPM seleccionado en la previsualización
    const getExistingStepsForPreviewSpm = () => {
        if (!previewFormSpm) return []
        const cleanSpm = previewFormSpm.trim().toUpperCase()
        const found = spms.find((s) => s?.spm?.toUpperCase() === cleanSpm)
        if (found && found.steps) {
            return found.steps
                .map((st) => st.stepName)
                .sort((a, b) =>
                    a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
                )
        }
        if (
            selectedSpm &&
			selectedSpm?.spm?.toUpperCase() === cleanSpm &&
			selectedSpm.steps
        ) {
            return selectedSpm.steps
                .map((st) => st.stepName)
                .sort((a, b) =>
                    a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
                )
        }
        return []
    }

    // Obtener lista de códigos SPM únicos para el autocompletado
    const getSpmOptions = () => {
        const optionsSet = new Set()

        // Agregar de los documentos existentes
        spms.forEach((s) => {
            if (s.spm) optionsSet.add(s.spm.toUpperCase())
        })

        // Agregar de las máquinas
        machines.forEach((m) => {
            if (m.idpminspeccion) optionsSet.add(m.idpminspeccion.toUpperCase())
            if (m.idpmmantencion) optionsSet.add(m.idpmmantencion.toUpperCase())
        })

        return Array.from(optionsSet).sort((a, b) =>
            a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
        )
    }

    // Función para normalizar y buscar similares
    const normalizeSpm = (spmInput) => {
        if (!spmInput) return 'GENERAL'

        const cleanVal = spmInput
            .trim()
            .toUpperCase()
            .replace(/[-_\s]/g, '')

        // Buscar si existe uno similar (ej: spm123 vs spm-123)
        const spmOpts = getSpmOptions()
        const similar = spmOpts.find(
            (opt) => opt.replace(/[-_\s]/g, '').toUpperCase() === cleanVal
        )
        return similar || cleanVal
    }

    // Al seleccionar SPM, cargar sus pasos con SAS urls
    const handleSpmSelect = async (spmCode) => {
        setLoading(true)
        setSelectedStepName('')
        setSelectedFile(null)
        setFileUrl('')
        revokeBlobUrls()
        try {
            if (isOnline) {
                const res = await documentsRoutes.getSpmDocumentSteps(spmCode)
                setSelectedSpm(res.data)
            } else {
                await documentationDatabase.initDbDocumentation()
                const localDoc = await documentationDatabase.getSpmStructure(spmCode)
                if (localDoc) {
                    const stepsWithLocalUrls = await Promise.all(
                        localDoc.steps.map(async (step) => {
                            const filesWithLocalUrls = await Promise.all(
                                step.files.map(async (file) => {
                                    const fileData = await documentationDatabase.getPdfFile(
                                        file.blobPath
                                    )
                                    if (fileData && fileData.blob) {
                                        const localUrl = URL.createObjectURL(fileData.blob)
                                        blobUrlsRef.current.push(localUrl)
                                        return {
                                            ...file,
                                            url: localUrl
                                        }
                                    }
                                    return file
                                })
                            )
                            return {
                                ...step,
                                files: filesWithLocalUrls
                            }
                        })
                    )
                    setSelectedSpm({
                        ...localDoc,
                        steps: stepsWithLocalUrls
                    })
                } else {
                    setSelectedSpm(null)
                }
            }
        } catch (error) {
            console.error('Error cargando pasos de SPM:', error)
        } finally {
            setLoading(false)
        }
    }

    // Al seleccionar Paso
    const handleStepSelect = (stepName) => {
        setSelectedStepName(stepName)
        setSelectedFile(null)
        setFileUrl('')
    }

    // Al seleccionar Archivo
    const handleFileSelect = (file) => {
        if (selectedFile && selectedFile.filename === file.filename) {
            setSelectedFile(null)
            setFileUrl('')
        } else {
            setSelectedFile(file)
            if (file && file.url) {
                const pdfUrl = file.url.includes('#') ? file.url : `${file.url}#navpanes=0`
                setFileUrl(pdfUrl)
            } else {
                setFileUrl('')
            }
        }
    }

    // Abrir menú de añadir documentación
    const handleAddClick = (event) => {
        setAnchorElMenu(event.currentTarget)
    }

    const handleOpenSpmUpload = (mode) => {
        setSpmUploadMode(mode)
        setSpmUploadOpen(true)
    }

    // Eliminaciones
    const handleDeleteSpm = async (spmCode, e) => {
        e.stopPropagation()
        triggerConfirm(
            `¿Está seguro de eliminar de forma permanente toda la documentación del SPM ${spmCode}? Esta acción no se puede deshacer.`,
            async () => {
                setLoading(true)
                try {
                    await documentsRoutes.deleteSpm(spmCode)
                    setStatus({
                        status: 'completed',
                        message: `Toda la documentación del SPM ${spmCode} ha sido eliminada.`,
                        severity: 'success'
                    })
                    await loadSpmDocuments()
                    if (selectedSpm && selectedSpm.spm === spmCode) {
                        setSelectedSpm(null)
                        setSelectedStepName('')
                        setSelectedFile(null)
                        setFileUrl('')
                    }
                } catch (error) {
                    const errorDetails = {
                        code: error.response?.status || 'Cliente',
                        info:
							error.response?.data?.message ||
							error.message ||
							'Error al eliminar SPM.'
                    }
                    setStatus({
                        status: 'error',
                        message: 'Error al eliminar SPM.',
                        errorDetails
                    })
                } finally {
                    setLoading(false)
                }
            }
        )
    }

    const handleDeleteStep = async (stepName, e) => {
        e.stopPropagation()
        triggerConfirm(
            `¿Está seguro de eliminar de forma permanente el paso "${stepName}" y todos sus archivos? Esta acción no se puede deshacer.`,
            async () => {
                setLoading(true)
                try {
                    await documentsRoutes.deleteSpmStep(selectedSpm.spm, stepName)
                    setStatus({
                        status: 'completed',
                        message: `El paso "${stepName}" ha sido eliminado.`,
                        severity: 'success'
                    })
                    // Recargar pasos
                    await handleSpmSelect(selectedSpm.spm)
                } catch (error) {
                    const errorDetails = {
                        code: error.response?.status || 'Cliente',
                        info:
							error.response?.data?.message ||
							error.message ||
							'Error al eliminar paso.'
                    }
                    setStatus({
                        status: 'error',
                        message: 'Error al eliminar paso.',
                        errorDetails
                    })
                } finally {
                    setLoading(false)
                }
            }
        )
    }

    const handleDeleteFile = async (filename, e) => {
        e.stopPropagation()
        triggerConfirm(
            `¿Está seguro de eliminar de forma permanente el archivo "${filename}"? Esta acción no se puede deshacer.`,
            async () => {
                setLoading(true)
                try {
                    await documentsRoutes.deleteSpmFile(
                        selectedSpm.spm,
                        selectedStepName,
                        filename
                    )
                    setStatus({
                        status: 'completed',
                        message: `El archivo "${filename}" ha sido eliminado.`,
                        severity: 'success'
                    })
                    // Recargar pasos para actualizar archivos
                    await handleSpmSelect(selectedSpm.spm)
                } catch (error) {
                    const errorDetails = {
                        code: error.response?.status || 'Cliente',
                        info:
							error.response?.data?.message ||
							error.message ||
							'Error al eliminar archivo.'
                    }
                    setStatus({
                        status: 'error',
                        message: 'Error al eliminar archivo.',
                        errorDetails
                    })
                } finally {
                    setLoading(false)
                }
            }
        )
    }

    const handleStartRenameSpm = (spmCode, e) => {
        e.stopPropagation()
        setRenameType('spm')
        setRenameCurrentValue(spmCode)
        setRenameNewValue(spmCode)
        setRenameTarget({ spm: spmCode })
        setOpenRenameDialog(true)
    }

    const handleStartRenameStep = (stepName, e) => {
        if (!selectedSpm) return
        e.stopPropagation()
        setRenameType('step')
        setRenameCurrentValue(stepName)
        setRenameNewValue(stepName)
        setRenameTarget({ spm: selectedSpm.spm, stepName })
        setOpenRenameDialog(true)
    }

    const handleStartRenameFile = (filename, e) => {
        if (!selectedSpm) return
        e.stopPropagation()
        setRenameType('file')
        const cleanName = filename.replace(/\.pdf$/i, '')
        setRenameCurrentValue(cleanName)
        setRenameNewValue(cleanName)
        setRenameTarget({
            spm: selectedSpm.spm,
            stepName: selectedStepName,
            filename
        })
        setOpenRenameDialog(true)
    }

    const handleConfirmRename = async () => {
        if (!renameNewValue || !renameNewValue.trim()) {
            triggerAlert('El nombre no puede estar vacío.')
            return
        }
        const cleanVal = renameNewValue.trim()

        setOpenRenameDialog(false)
        setLoading(true)

        try {
            if (renameType === 'spm') {
                const targetSpm = renameTarget.spm
                await documentsRoutes.renameSpm(targetSpm, cleanVal)
                setStatus({
                    status: 'completed',
                    message: `Código SPM renombrado a ${cleanVal} exitosamente.`,
                    severity: 'success'
                })
                await loadSpmDocuments()
                if (selectedSpm && selectedSpm.spm === targetSpm) {
                    await handleSpmSelect(cleanVal)
                }
            } else if (renameType === 'step') {
                const { spm, stepName } = renameTarget
                await documentsRoutes.renameSpmStep(spm, stepName, cleanVal)
                setStatus({
                    status: 'completed',
                    message: `Paso renombrado a ${cleanVal} exitosamente.`,
                    severity: 'success'
                })
                await handleSpmSelect(spm)
                if (selectedStepName === stepName) {
                    setSelectedStepName(cleanVal)
                }
            } else if (renameType === 'file') {
                const { spm, stepName, filename } = renameTarget
                let newFileVal = cleanVal
                if (!newFileVal.toLowerCase().endsWith('.pdf')) {
                    newFileVal += '.pdf'
                }
                await documentsRoutes.renameSpmFile(
                    spm,
                    stepName,
                    filename,
                    newFileVal
                )
                setStatus({
                    status: 'completed',
                    message: `Archivo renombrado a ${newFileVal} exitosamente.`,
                    severity: 'success'
                })
                await handleSpmSelect(spm)
                setSelectedFile(null)
                setFileUrl('')
            }
        } catch (error) {
            console.error('Error al renombrar:', error)
            const errorDetails = {
                code: error.response?.status || 'Cliente',
                info:
					error.response?.data?.message ||
					error.message ||
					'Error al renombrar el elemento.'
            }
            setStatus({
                status: 'error',
                message: 'Error al renombrar el elemento.',
                errorDetails
            })
        } finally {
            setLoading(false)
            setRenameTarget(null)
            setRenameNewValue('')
            setRenameCurrentValue('')
        }
    }

    // Imprimir
    const handlePrint = () => {
        if (fileUrl) {
            window.open(fileUrl, '_blank')
        }
    }

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
                <DialogTitle
                    style={{
                        backgroundColor: '#F4F4F4',
                        padding: '12px 24px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <Box display="flex" alignItems="center">
                        <FontAwesomeIcon
                            icon={faFolderOpen}
                            style={{ marginRight: 10, color: '#3f51b5' }}
                        />
                        <Typography
                            variant="h6"
                            component="span"
                            style={{ fontWeight: 600 }}
                        >
                            {spmCode
                                ? `Documentación Técnica - ${spmCode}`
                                : 'Documentos Disponibles'}
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={2}>
                        {!readOnly && isOnline && (
                            <>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={editMode}
                                            onChange={(e) => setEditMode(e.target.checked)}
                                            color="secondary"
                                            size="small"
                                        />
                                    }
                                    label={
                                        <Typography variant="body2" style={{ fontWeight: 500 }}>
											Modo Edición
                                        </Typography>
                                    }
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<FontAwesomeIcon icon={faFilePdf} />}
                                    onClick={() => handleOpenSpmUpload('file')}
                                    disabled={loading}
                                    size="small"
                                    style={{ borderRadius: 8, textTransform: 'none', marginRight: 8 }}
                                >
									Subir Archivos
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<FontAwesomeIcon icon={faFolder} />}
                                    onClick={() => handleOpenSpmUpload('folder')}
                                    disabled={loading}
                                    size="small"
                                    style={{ borderRadius: 8, textTransform: 'none' }}
                                >
									Subir Carpeta
                                </Button>
                            </>
                        )}
                        <IconButton size="small" onClick={handleClose}>
                            <FontAwesomeIcon icon={faTimes} />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent
                    dividers
                    style={{ minHeight: '500px', padding: 16, position: 'relative' }}
                >
                    {loading && spms.length > 0 && (
                        <Box
                            position="absolute"
                            top={0}
                            left={0}
                            right={0}
                            bottom={0}
                            bgcolor="rgba(255, 255, 255, 0.7)"
                            zIndex={10}
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <CircularProgress size={40} />
                            <Typography
                                variant="body2"
                                style={{ marginTop: 15, fontWeight: 600, color: '#3f51b5' }}
                            >
								Procesando...
                            </Typography>
                        </Box>
                    )}

                    {loading && spms.length === 0 ? (
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            height="400px"
                        >
                            <CircularProgress />
                            <Typography
                                variant="body2"
                                style={{ marginTop: 15, color: '#666' }}
                            >
								Cargando documentos...
                            </Typography>
                        </Box>
                    ) : (
                        (() => {
                            // 1. Array de SPMs ordenados de forma inteligente (numérica y alfabéticamente)
                            const sortedSpms = [...spms].sort((a, b) =>
                                a.spm.localeCompare(b.spm, undefined, {
                                    numeric: true,
                                    sensitivity: 'base'
                                })
                            )

                            // 2. Array de Pasos del SPM seleccionado ordenados de forma inteligente
                            const sortedSteps = selectedSpm?.steps
                                ? [...selectedSpm.steps].sort((a, b) =>
                                    a.stepName.localeCompare(b.stepName, undefined, {
                                        numeric: true,
                                        sensitivity: 'base'
                                    })
                                )
                                : []

                            // Definimos las fases de navegación basadas en las selecciones
                            const hasSpmSelected = !!selectedSpm
                            const hasStepSelected = !!selectedStepName

                            // FASE 2: Pasos & Documentos (Cuando se selecciona un paso)
                            if (hasStepSelected) {
                                const stepFiles =
									selectedSpm?.steps?.find(
									    (s) => s.stepName === selectedStepName
									)?.files || []
                                const hasFileSelected = !!selectedFile

                                if (hasFileSelected) {
                                    // FASE 3: Documentos (ancho 3) & Vista Previa (ancho 9)
                                    return (
                                        <Grid container spacing={2}>
                                            {/* Columna Izquierda (ancho 2): Documentos del paso */}
                                            <Grid item xs={12} sm={2}>
                                                <Paper
                                                    variant="outlined"
                                                    style={{
                                                        height: '480px',
                                                        overflowY: 'auto',
                                                        borderRadius: 8
                                                    }}
                                                >
                                                    <Box
                                                        p={1.5}
                                                        bgcolor="#E0E0E0"
                                                        borderBottom="1px solid #ccc"
                                                        display="flex"
                                                        alignItems="center"
                                                        gap={1}
                                                    >
                                                        <Button
                                                            size="small"
                                                            variant="text"
                                                            onClick={() => setSelectedFile(null)}
                                                            style={{
                                                                minWidth: 0,
                                                                padding: '2px 6px',
                                                                textTransform: 'none',
                                                                fontWeight: 600
                                                            }}
                                                        >
															&larr; Guías
                                                        </Button>
                                                        <Typography
                                                            variant="subtitle2"
                                                            style={{
                                                                fontWeight: 600,
                                                                color: '#333',
                                                                fontSize: 12
                                                            }}
                                                        >
															Pasos / Documentos
                                                        </Typography>
                                                    </Box>
                                                    <List dense>
                                                        {stepFiles.map((file) => {
                                                            const isSelected =
																selectedFile?.filename === file.filename
                                                            return (
                                                                <ListItem
                                                                    button
                                                                    key={file.filename}
                                                                    selected={isSelected}
                                                                    disabled={loading || uploading || !isOnline}
                                                                    onClick={() =>
                                                                        !loading &&
																		!uploading &&
																		handleFileSelect(file)
                                                                    }
                                                                    style={{
                                                                        borderBottom: '1px solid #F0F0F0',
                                                                        opacity: loading || uploading ? 0.5 : 1,
                                                                        pointerEvents:
																			loading || uploading ? 'none' : 'auto'
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon
                                                                        icon={faFilePdf}
                                                                        style={{
                                                                            marginRight: 8,
                                                                            color: '#E53935',
                                                                            fontSize: 13
                                                                        }}
                                                                    />
                                                                    <ListItemText
                                                                        primary={file.filename}
                                                                        primaryTypographyProps={{
                                                                            style: {
                                                                                fontSize: 11.5,
                                                                                fontWeight: isSelected ? 700 : 500,
                                                                                whiteSpace: 'nowrap',
                                                                                overflow: 'hidden',
                                                                                textOverflow: 'ellipsis'
                                                                            }
                                                                        }}
                                                                        title={file.filename}
                                                                    />
                                                                    {editMode && (
                                                                        <ListItemSecondaryAction>
                                                                            <IconButton
                                                                                edge="end"
                                                                                size="small"
                                                                                color="primary"
                                                                                onClick={(e) =>
                                                                                    handleStartRenameFile(
                                                                                        file.filename,
                                                                                        e
                                                                                    )
                                                                                }
                                                                                disabled={
                                                                                    loading || uploading || !isOnline
                                                                                }
                                                                                style={{ marginRight: 8 }}
                                                                            >
                                                                                <FontAwesomeIcon
                                                                                    icon={faEdit}
                                                                                    size="xs"
                                                                                />
                                                                            </IconButton>
                                                                            <IconButton
                                                                                edge="end"
                                                                                size="small"
                                                                                color="error"
                                                                                onClick={(e) =>
                                                                                    handleDeleteFile(file.filename, e)
                                                                                }
                                                                                disabled={
                                                                                    loading || uploading || !isOnline
                                                                                }
                                                                            >
                                                                                <FontAwesomeIcon
                                                                                    icon={faTrash}
                                                                                    size="xs"
                                                                                />
                                                                            </IconButton>
                                                                        </ListItemSecondaryAction>
                                                                    )}
                                                                </ListItem>
                                                            )
                                                        })}
                                                    </List>
                                                </Paper>
                                            </Grid>

                                            {/* Columna Derecha (ancho 10): Vista Previa del PDF */}
                                            <Grid item xs={12} sm={10}>
                                                <Paper
                                                    variant="outlined"
                                                    style={{
                                                        height: '480px',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        borderRadius: 8,
                                                        overflow: 'hidden'
                                                    }}
                                                >
                                                    <Box
                                                        flexGrow={1}
                                                        bgcolor="#fff"
                                                        overflow="hidden"
                                                        position="relative"
                                                    >
                                                        {fileUrl ? (
                                                            <iframe
                                                                src={fileUrl}
                                                                width="100%"
                                                                height="100%"
                                                                title="Previsualizador de PDF"
                                                                style={{
                                                                    border: 'none',
                                                                    backgroundColor: '#fff'
                                                                }}
                                                            />
                                                        ) : (
                                                            <Box
                                                                display="flex"
                                                                flexDirection="column"
                                                                alignItems="center"
                                                                justifyContent="center"
                                                                height="100%"
                                                                color="#999"
                                                            >
                                                                <CircularProgress size={24} />
                                                                <Typography
                                                                    variant="caption"
                                                                    style={{ marginTop: 8 }}
                                                                >
																	Cargando PDF...
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Paper>
                                            </Grid>
                                        </Grid>
                                    )
                                }

                                // FASE 2: Pasos (ancho 5) & Documentos (ancho 7)
                                return (
                                    <Grid container spacing={2}>
                                        {/* Columna Izquierda (ancho 4): Pasos de la guía */}
                                        <Grid item xs={12} sm={4}>
                                            <Paper
                                                variant="outlined"
                                                style={{
                                                    height: '480px',
                                                    overflowY: 'auto',
                                                    borderRadius: 8
                                                }}
                                            >
                                                <Box
                                                    p={1.5}
                                                    bgcolor="#E0E0E0"
                                                    borderBottom="1px solid #ccc"
                                                    display="flex"
                                                    alignItems="center"
                                                    gap={1}
                                                >
                                                    {!spmCode && (
                                                        <Button
                                                            size="small"
                                                            variant="text"
                                                            onClick={() => {
                                                                setSelectedStepName('')
                                                                setSelectedFile(null)
                                                            }}
                                                            style={{
                                                                minWidth: 0,
                                                                padding: '2px 6px',
                                                                textTransform: 'none',
                                                                fontWeight: 600
                                                            }}
                                                        >
															&larr; Carpetas
                                                        </Button>
                                                    )}
                                                    <Typography
                                                        variant="subtitle2"
                                                        style={{ fontWeight: 600, color: '#333' }}
                                                    >
														Guías
                                                    </Typography>
                                                </Box>
                                                <List dense>
                                                    {sortedSteps.map((step) => (
                                                        <ListItem
                                                            button
                                                            key={step.stepName}
                                                            selected={selectedStepName === step.stepName}
                                                            disabled={loading || uploading || !isOnline}
                                                            onClick={() =>
                                                                !loading &&
																!uploading &&
																handleStepSelect(step.stepName)
                                                            }
                                                            style={{
                                                                borderBottom: '1px solid #F0F0F0',
                                                                opacity: loading || uploading ? 0.5 : 1,
                                                                pointerEvents:
																	loading || uploading ? 'none' : 'auto'
                                                            }}
                                                        >
                                                            <ListItemText
                                                                primary={step.stepName}
                                                                secondary={`${step.files?.length || 0} docs`}
                                                                primaryTypographyProps={{
                                                                    style: { fontWeight: 600 }
                                                                }}
                                                            />
                                                            {editMode && (
                                                                <ListItemSecondaryAction>
                                                                    <IconButton
                                                                        edge="end"
                                                                        size="small"
                                                                        color="primary"
                                                                        onClick={(e) =>
                                                                            handleStartRenameStep(step.stepName, e)
                                                                        }
                                                                        disabled={loading || uploading || !isOnline}
                                                                        style={{ marginRight: 8 }}
                                                                    >
                                                                        <FontAwesomeIcon icon={faEdit} size="xs" />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        edge="end"
                                                                        size="small"
                                                                        color="error"
                                                                        onClick={(e) =>
                                                                            handleDeleteStep(step.stepName, e)
                                                                        }
                                                                        disabled={loading || uploading || !isOnline}
                                                                    >
                                                                        <FontAwesomeIcon icon={faTrash} size="xs" />
                                                                    </IconButton>
                                                                </ListItemSecondaryAction>
                                                            )}
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Paper>
                                        </Grid>

                                        {/* Columna Derecha (ancho 8): Documentos del paso */}
                                        <Grid item xs={12} sm={8}>
                                            <Paper
                                                variant="outlined"
                                                style={{
                                                    height: '480px',
                                                    overflowY: 'auto',
                                                    borderRadius: 8
                                                }}
                                            >
                                                <List dense>
                                                    {stepFiles.map((file) => {
                                                        const isSelected =
															selectedFile?.filename === file.filename
                                                        return (
                                                            <ListItem
                                                                button
                                                                key={file.filename}
                                                                selected={isSelected}
                                                                disabled={loading || uploading || !isOnline}
                                                                onClick={() =>
                                                                    !loading &&
																	!uploading &&
																	handleFileSelect(file)
                                                                }
                                                                style={{
                                                                    borderBottom: '1px solid #F0F0F0',
                                                                    opacity: loading || uploading ? 0.5 : 1,
                                                                    pointerEvents:
																		loading || uploading ? 'none' : 'auto'
                                                                }}
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={faFilePdf}
                                                                    style={{ marginRight: 8, color: '#E53935' }}
                                                                />
                                                                <ListItemText
                                                                    primary={file.filename}
                                                                    primaryTypographyProps={{
                                                                        style: { fontSize: 13, fontWeight: 500 }
                                                                    }}
                                                                />
                                                                {editMode && (
                                                                    <ListItemSecondaryAction>
                                                                        <IconButton
                                                                            edge="end"
                                                                            size="small"
                                                                            color="primary"
                                                                            onClick={(e) =>
                                                                                handleStartRenameFile(file.filename, e)
                                                                            }
                                                                            disabled={
                                                                                loading || uploading || !isOnline
                                                                            }
                                                                            style={{ marginRight: 8 }}
                                                                        >
                                                                            <FontAwesomeIcon
                                                                                icon={faEdit}
                                                                                size="xs"
                                                                            />
                                                                        </IconButton>
                                                                        <IconButton
                                                                            edge="end"
                                                                            size="small"
                                                                            color="error"
                                                                            onClick={(e) =>
                                                                                handleDeleteFile(file.filename, e)
                                                                            }
                                                                            disabled={
                                                                                loading || uploading || !isOnline
                                                                            }
                                                                        >
                                                                            <FontAwesomeIcon
                                                                                icon={faTrash}
                                                                                size="xs"
                                                                            />
                                                                        </IconButton>
                                                                    </ListItemSecondaryAction>
                                                                )}
                                                            </ListItem>
                                                        )
                                                    })}
                                                    {stepFiles.length === 0 && (
                                                        <Box p={3} textAlign="center">
                                                            <Typography variant="body2" color="textSecondary">
																No hay documentos en este paso.
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </List>
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                )
                            }

                            // FASE 1: Carpetas & Pasos (Cuando no hay paso seleccionado)
                            if (!spmCode) {
                                // Flujo Administrador (Sin SPM fijo)
                                if (!hasSpmSelected) {
                                    // Estado Inicial Admin: Solo carpetas en ancho completo (xs={12})
                                    return (
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Paper
                                                    variant="outlined"
                                                    style={{
                                                        height: '480px',
                                                        overflowY: 'auto',
                                                        borderRadius: 8
                                                    }}
                                                >
                                                    <Box
                                                        p={1.5}
                                                        bgcolor="#E0E0E0"
                                                        borderBottom="1px solid #ccc"
                                                    >
                                                        <Typography
                                                            variant="subtitle2"
                                                            style={{ fontWeight: 600, color: '#333' }}
                                                        >
															Carpetas SPM disponibles ({spms.length})
                                                        </Typography>
                                                    </Box>
                                                    <List dense>
                                                        {sortedSpms.map((s) => (
                                                            <ListItem
                                                                button
                                                                key={s.spm}
                                                                disabled={loading || uploading || !isOnline}
                                                                onClick={() =>
                                                                    !loading &&
																	!uploading &&
																	handleSpmSelect(s.spm)
                                                                }
                                                                style={{
                                                                    borderBottom: '1px solid #F0F0F0',
                                                                    opacity: loading || uploading ? 0.5 : 1,
                                                                    pointerEvents:
																		loading || uploading ? 'none' : 'auto'
                                                                }}
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={faFolder}
                                                                    style={{
                                                                        marginRight: 15,
                                                                        color: '#FFA000',
                                                                        fontSize: 18
                                                                    }}
                                                                />
                                                                <ListItemText
                                                                    primary={s.spm}
                                                                    secondary={
                                                                        s.spmType === 'inspeccion'
                                                                            ? 'Pauta de Inspección'
                                                                            : 'Pauta de Mantención'
                                                                    }
                                                                    primaryTypographyProps={{
                                                                        style: { fontWeight: 600, fontSize: 14 }
                                                                    }}
                                                                />
                                                                {editMode && (
                                                                    <ListItemSecondaryAction>
                                                                        <IconButton
                                                                            edge="end"
                                                                            size="small"
                                                                            color="primary"
                                                                            onClick={(e) =>
                                                                                handleStartRenameSpm(s.spm, e)
                                                                            }
                                                                            disabled={
                                                                                loading || uploading || !isOnline
                                                                            }
                                                                            style={{ marginRight: 8 }}
                                                                        >
                                                                            <FontAwesomeIcon
                                                                                icon={faEdit}
                                                                                size="xs"
                                                                            />
                                                                        </IconButton>
                                                                        <IconButton
                                                                            edge="end"
                                                                            size="small"
                                                                            color="error"
                                                                            onClick={(e) => handleDeleteSpm(s.spm, e)}
                                                                            disabled={
                                                                                loading || uploading || !isOnline
                                                                            }
                                                                        >
                                                                            <FontAwesomeIcon
                                                                                icon={faTrash}
                                                                                size="xs"
                                                                            />
                                                                        </IconButton>
                                                                    </ListItemSecondaryAction>
                                                                )}
                                                            </ListItem>
                                                        ))}
                                                        {spms.length === 0 && (
                                                            <Box p={3} textAlign="center">
                                                                <Typography
                                                                    variant="body2"
                                                                    color="textSecondary"
                                                                >
																	No hay carpetas SPM cargadas. Importe una
																	carpeta para comenzar.
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </List>
                                                </Paper>
                                            </Grid>
                                        </Grid>
                                    )
                                }

                                // Si hay SPM seleccionado: Carpetas (ancho 5) + Pasos (ancho 7)
                                return (
                                    <Grid container spacing={2}>
                                        {/* Columna Izquierda (ancho 5): Carpetas SPM */}
                                        <Grid item xs={12} sm={5}>
                                            <Paper
                                                variant="outlined"
                                                style={{
                                                    height: '480px',
                                                    overflowY: 'auto',
                                                    borderRadius: 8
                                                }}
                                            >
                                                <Box
                                                    p={1.5}
                                                    bgcolor="#E0E0E0"
                                                    borderBottom="1px solid #ccc"
                                                >
                                                    <Typography
                                                        variant="subtitle2"
                                                        style={{ fontWeight: 600, color: '#333' }}
                                                    >
														Carpetas SPM ({spms.length})
                                                    </Typography>
                                                </Box>
                                                <List dense>
                                                    {sortedSpms.map((s) => (
                                                        <ListItem
                                                            button
                                                            key={s.spm}
                                                            selected={selectedSpm?.spm === s.spm}
                                                            disabled={loading || uploading || !isOnline}
                                                            onClick={() =>
                                                                !loading && !uploading && handleSpmSelect(s.spm)
                                                            }
                                                            style={{
                                                                borderBottom: '1px solid #F0F0F0',
                                                                opacity: loading || uploading ? 0.5 : 1,
                                                                pointerEvents:
																	loading || uploading ? 'none' : 'auto'
                                                            }}
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faFolder}
                                                                style={{ marginRight: 8, color: '#FFA000' }}
                                                            />
                                                            <ListItemText
                                                                primary={s.spm}
                                                                secondary={
                                                                    s.spmType === 'inspeccion'
                                                                        ? 'Inspección'
                                                                        : 'Mantención'
                                                                }
                                                                primaryTypographyProps={{
                                                                    style: { fontWeight: 500 }
                                                                }}
                                                            />
                                                            {editMode && (
                                                                <ListItemSecondaryAction>
                                                                    <IconButton
                                                                        edge="end"
                                                                        size="small"
                                                                        color="primary"
                                                                        onClick={(e) =>
                                                                            handleStartRenameSpm(s.spm, e)
                                                                        }
                                                                        disabled={loading || uploading || !isOnline}
                                                                        style={{ marginRight: 8 }}
                                                                    >
                                                                        <FontAwesomeIcon icon={faEdit} size="xs" />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        edge="end"
                                                                        size="small"
                                                                        color="error"
                                                                        onClick={(e) => handleDeleteSpm(s.spm, e)}
                                                                        disabled={loading || uploading || !isOnline}
                                                                    >
                                                                        <FontAwesomeIcon icon={faTrash} size="xs" />
                                                                    </IconButton>
                                                                </ListItemSecondaryAction>
                                                            )}
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Paper>
                                        </Grid>

                                        {/* Columna Derecha (ancho 7): Pasos del SPM seleccionado */}
                                        <Grid item xs={12} sm={7}>
                                            <Paper
                                                variant="outlined"
                                                style={{
                                                    height: '480px',
                                                    overflowY: 'auto',
                                                    borderRadius: 8
                                                }}
                                            >
                                                <List dense>
                                                    {sortedSteps.map((step) => (
                                                        <ListItem
                                                            button
                                                            key={step.stepName}
                                                            disabled={loading || uploading || !isOnline}
                                                            onClick={() =>
                                                                !loading &&
																!uploading &&
																handleStepSelect(step.stepName)
                                                            }
                                                            style={{
                                                                borderBottom: '1px solid #F0F0F0',
                                                                opacity: loading || uploading ? 0.5 : 1,
                                                                pointerEvents:
																	loading || uploading ? 'none' : 'auto'
                                                            }}
                                                        >
                                                            <ListItemText
                                                                primary={step.stepName}
                                                                secondary={`${step.files?.length || 0} docs`}
                                                                primaryTypographyProps={{
                                                                    style: { fontWeight: 500 }
                                                                }}
                                                            />
                                                            {editMode && (
                                                                <ListItemSecondaryAction>
                                                                    <IconButton
                                                                        edge="end"
                                                                        size="small"
                                                                        color="primary"
                                                                        onClick={(e) =>
                                                                            handleStartRenameStep(step.stepName, e)
                                                                        }
                                                                        disabled={loading || uploading || !isOnline}
                                                                        style={{ marginRight: 8 }}
                                                                    >
                                                                        <FontAwesomeIcon icon={faEdit} size="xs" />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        edge="end"
                                                                        size="small"
                                                                        color="error"
                                                                        onClick={(e) =>
                                                                            handleDeleteStep(step.stepName, e)
                                                                        }
                                                                        disabled={loading || uploading || !isOnline}
                                                                    >
                                                                        <FontAwesomeIcon icon={faTrash} size="xs" />
                                                                    </IconButton>
                                                                </ListItemSecondaryAction>
                                                            )}
                                                        </ListItem>
                                                    ))}
                                                    {(!selectedSpm.steps ||
														selectedSpm.steps.length === 0) && (
                                                        <Box p={3} textAlign="center">
                                                            <Typography variant="body2" color="textSecondary">
																Este SPM no tiene pasos configurados.
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </List>
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                )
                            } else {
                                // Flujo Operador (SPM Fijo. Solo pasos en ancho completo xs={12} porque no ha seleccionado paso aún)
                                return (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Paper
                                                variant="outlined"
                                                style={{
                                                    height: '480px',
                                                    overflowY: 'auto',
                                                    borderRadius: 8
                                                }}
                                            >
                                                <Box
                                                    p={1.5}
                                                    bgcolor="#E0E0E0"
                                                    borderBottom="1px solid #ccc"
                                                >
                                                    <Typography
                                                        variant="subtitle2"
                                                        style={{ fontWeight: 600, color: '#333' }}
                                                    >
														Guías Disponibles ({selectedSpm?.steps?.length || 0})
                                                    </Typography>
                                                </Box>
                                                <List dense>
                                                    {sortedSteps.map((step) => (
                                                        <ListItem
                                                            button
                                                            key={step.stepName}
                                                            disabled={loading || uploading || !isOnline}
                                                            onClick={() =>
                                                                !loading &&
																!uploading &&
																handleStepSelect(step.stepName)
                                                            }
                                                            style={{
                                                                borderBottom: '1px solid #F0F0F0',
                                                                opacity: loading || uploading ? 0.5 : 1,
                                                                pointerEvents:
																	loading || uploading ? 'none' : 'auto'
                                                            }}
                                                        >
                                                            <ListItemText
                                                                primary={step.stepName}
                                                                secondary={`${step.files?.length || 0} docs`}
                                                                primaryTypographyProps={{
                                                                    style: { fontWeight: 500 }
                                                                }}
                                                            />
                                                        </ListItem>
                                                    ))}
                                                    {(!selectedSpm?.steps ||
														selectedSpm.steps.length === 0) && (
                                                        <Box p={3} textAlign="center">
                                                            <Typography variant="body2" color="textSecondary">
																No hay pasos disponibles en este SPM.
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </List>
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                )
                            }
                        })()
                    )}
                </DialogContent>

                <DialogActions
                    style={{ backgroundColor: '#F4F4F4', padding: '12px 24px' }}
                >
                    <Button
                        onClick={handleClose}
                        color="primary"
                        variant="outlined"
                        style={{ borderRadius: 8, textTransform: 'none' }}
                    >
						Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
            <SpmUploadDialog
                open={spmUploadOpen}
                handleClose={() => {
                    setSpmUploadOpen(false)
                    loadSpmDocuments()
                    if (selectedSpm) {
                        handleSpmSelect(selectedSpm.spm)
                    }
                }}
                defaultSpm={selectedSpm?.spm || ''}
                defaultStep={selectedStepName || ''}
                mode={spmUploadMode}
            />

            {/* Diálogos personalizados de Confirmación y Alerta del Proyecto */}
            <ConfirmarDialog
                open={confirmConfig.open}
                handleClose={
                    confirmConfig.onCancel ||
					(() => setConfirmConfig((prev) => ({ ...prev, open: false })))
                }
                message={confirmConfig.message}
                select={confirmConfig.onConfirm}
            />

            <AlertDialog
                open={alertConfig.open}
                title={alertConfig.title}
                message={alertConfig.message}
                onClose={() => setAlertConfig((prev) => ({ ...prev, open: false }))}
                actions={[
                    {
                        text: 'Aceptar',
                        handler: () => setAlertConfig((prev) => ({ ...prev, open: false })),
                        props: {
                            variant: 'contained',
                            color: 'primary',
                            style: { borderRadius: 8 }
                        }
                    }
                ]}
            />

            {/* Diálogo para Renombrar Carpetas, Pasos o Archivos */}
            <Dialog
                open={openRenameDialog}
                onClose={() => setOpenRenameDialog(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle style={{ fontWeight: 600 }}>
                    {renameType === 'spm'
                        ? 'Renombrar Carpeta SPM'
                        : renameType === 'step'
                            ? 'Renombrar Paso'
                            : 'Renombrar Archivo'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText style={{ marginBottom: 15 }}>
						Ingrese el nuevo nombre para el elemento. Esto renombrará los
						archivos correspondientes en el servidor.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Nuevo Nombre"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={renameNewValue}
                        onChange={(e) => setRenameNewValue(e.target.value)}
                        placeholder="Escriba el nuevo nombre aquí..."
                    />
                </DialogContent>
                <DialogActions style={{ padding: '12px 24px' }}>
                    <Button
                        onClick={() => setOpenRenameDialog(false)}
                        color="inherit"
                        variant="outlined"
                        style={{ borderRadius: 8, textTransform: 'none' }}
                    >
						Cancelar
                    </Button>
                    <Button
                        onClick={handleConfirmRename}
                        color="primary"
                        variant="contained"
                        disabled={
                            !renameNewValue ||
							!renameNewValue.trim() ||
							renameNewValue.trim() === renameCurrentValue
                        }
                        style={{ borderRadius: 8, textTransform: 'none' }}
                    >
						Guardar
                    </Button>
                </DialogActions>
            </Dialog>

        </>
    )
}

export default SpmDocumentsDialog
